import React, { useState, useEffect } from 'react';
import { Card, Table, Button, Badge, Modal, Form, Row, Col, Alert } from 'react-bootstrap';
import { adminAPI, menuAPI } from '../../services/api';
import { MENU_CATEGORIES, EMPTY_MENU } from '../../utils/constants';

function ItemLocksTab({ message, setMessage }) {
  const [locks, setLocks] = useState([]);
  const [menuItems, setMenuItems] = useState(EMPTY_MENU);
  const [daysOfWeek, setDaysOfWeek] = useState([]);
  const [timeSlots, setTimeSlots] = useState([]);
  const [showAddModal, setShowAddModal] = useState(false);
  const [newLock, setNewLock] = useState({
    menuItemId: '',
    dayOfWeekId: '',
    timeSlotId: ''
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [locksRes, menuRes, daysRes, slotsRes] = await Promise.all([
        adminAPI.getAllItemLocks(),
        menuAPI.getMenuItems(),
        menuAPI.getDaysOfWeek(),
        menuAPI.getTimeSlots()
      ]);

      setLocks(locksRes.data);
      setMenuItems(menuRes.data);
      setDaysOfWeek(daysRes.data);
      setTimeSlots(slotsRes.data);
    } catch (error) {
      console.error('Failed to load data:', error);
      setMessage({ type: 'danger', text: 'Failed to load data' });
    }
  };

  const handleAddLock = async () => {
    try {
      if (!newLock.menuItemId) {
        setMessage({ type: 'warning', text: 'Please select a menu item' });
        return;
      }

      await adminAPI.addItemLock({
        menuItemId: parseInt(newLock.menuItemId),
        dayOfWeekId: newLock.dayOfWeekId ? parseInt(newLock.dayOfWeekId) : null,
        timeSlotId: newLock.timeSlotId ? parseInt(newLock.timeSlotId) : null
      });

      setMessage({ type: 'success', text: 'Lock added successfully' });
      setShowAddModal(false);
      setNewLock({ menuItemId: '', dayOfWeekId: '', timeSlotId: '' });
      loadData();
    } catch (error) {
      console.error('Failed to add lock:', error);
      setMessage({ 
        type: 'danger', 
        text: error.response?.data?.error || 'Failed to add lock' 
      });
    }
  };

  const handleRemoveLock = async (lockId, itemName, scope) => {
    if (!window.confirm(`Remove lock for ${itemName} (${scope})?`)) {
      return;
    }

    try {
      await adminAPI.removeItemLock(lockId);
      setMessage({ type: 'success', text: 'Lock removed successfully' });
      loadData();
    } catch (error) {
      console.error('Failed to remove lock:', error);
      setMessage({ type: 'danger', text: 'Failed to remove lock' });
    }
  };

  const getLockScope = (lock) => {
    if (lock.day_name && lock.slot_name) {
      return `${lock.day_name} - ${lock.slot_name}`;
    } else if (lock.day_name) {
      return `${lock.day_name} (All Slots)`;
    } else if (lock.slot_name) {
      return `${lock.slot_name} (All Days)`;
    } else {
      return 'All Days, All Slots';
    }
  };

  // Group locks by menu item
  const locksByItem = locks.reduce((acc, lock) => {
    if (!acc[lock.menu_item_id]) {
      acc[lock.menu_item_id] = [];
    }
    acc[lock.menu_item_id].push(lock);
    return acc;
  }, {});

  return (
    <>
      <Card className="fade-in mb-4">
        <Card.Header>
          🔒 Granular Menu Item Locks
          <Button
            variant="light"
            size="sm"
            className="float-end"
            onClick={() => setShowAddModal(true)}
          >
            + Add Lock
          </Button>
        </Card.Header>
        <Card.Body>
          <Alert variant="info" className="mb-4">
            <strong>Granular Locking:</strong> Lock items for specific days and time slots.
            <div className="mt-2">
              <strong>🔒 What locking does:</strong> Customers can adjust locked items between 0 and their 
              standing order quantity, but cannot <em>exceed</em> the standing order amount. This prevents 
              last-minute additions while allowing flexibility to reduce orders.
            </div>
            <ul className="mt-2 mb-0">
              <li><strong>Specific:</strong> Monday Morning only</li>
              <li><strong>Day-wide:</strong> All Friday slots</li>
              <li><strong>Slot-wide:</strong> Morning on all days</li>
              <li><strong>Global:</strong> All days, all slots</li>
            </ul>
          </Alert>

          {MENU_CATEGORIES.map(category => (
            <div key={category} className="mb-4">
              <h5 className="mb-3">{category} Items</h5>
              <Table striped hover size="sm">
                <thead>
                  <tr>
                    <th>Item Name</th>
                    <th>Current Locks</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {menuItems[category]?.map(item => {
                    const itemLocks = locksByItem[item.id] || [];
                    
                    return (
                      <tr key={item.id}>
                        <td><strong>{item.item_name}</strong></td>
                        <td>
                          {itemLocks.length === 0 ? (
                            <Badge bg="success">No Locks (Editable)</Badge>
                          ) : (
                            itemLocks.map(lock => (
                              <Badge 
                                key={lock.id} 
                                bg="warning" 
                                className="me-2 mb-1"
                                style={{ fontSize: '0.85rem' }}
                              >
                                🔒 {getLockScope(lock)}
                              </Badge>
                            ))
                          )}
                        </td>
                        <td>
                          {itemLocks.map(lock => (
                            <Button
                              key={lock.id}
                              variant="outline-danger"
                              size="sm"
                              className="me-1 mb-1"
                              onClick={() => handleRemoveLock(
                                lock.id, 
                                item.item_name, 
                                getLockScope(lock)
                              )}
                            >
                              Remove {getLockScope(lock)}
                            </Button>
                          ))}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </Table>
            </div>
          ))}

          {locks.length === 0 && (
            <Alert variant="secondary">
              No locks configured. All items are editable for all days and time slots.
            </Alert>
          )}
        </Card.Body>
      </Card>

      {/* Add Lock Modal */}
      <Modal show={showAddModal} onHide={() => setShowAddModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Add Menu Item Lock</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Alert variant="info">
            Configure when this item should be locked for order entry.
            <br />
            <small className="text-muted">
              Locked items: Customers can adjust between 0 and their standing order quantity, but cannot <strong>exceed</strong> it.
            </small>
            <br />
            <small className="text-muted">Leave Day or Slot blank to lock for all days/slots.</small>
          </Alert>

          <Form>
            <Form.Group className="mb-3">
              <Form.Label><strong>Menu Item *</strong></Form.Label>
              <Form.Select
                value={newLock.menuItemId}
                onChange={(e) => setNewLock({ ...newLock, menuItemId: e.target.value })}
                required
              >
                <option value="">-- Select Item --</option>
                {Object.entries(menuItems).map(([category, items]) => (
                  <optgroup key={category} label={`${category} Items`}>
                    {items.map(item => (
                      <option key={item.id} value={item.id}>
                        {item.item_name}
                      </option>
                    ))}
                  </optgroup>
                ))}
              </Form.Select>
            </Form.Group>

            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label><strong>Day of Week</strong> (optional)</Form.Label>
                  <Form.Select
                    value={newLock.dayOfWeekId}
                    onChange={(e) => setNewLock({ ...newLock, dayOfWeekId: e.target.value })}
                  >
                    <option value="">All Days</option>
                    {daysOfWeek.map(day => (
                      <option key={day.id} value={day.id}>
                        {day.day_name}
                      </option>
                    ))}
                  </Form.Select>
                  <Form.Text className="text-muted">
                    Leave blank to lock for all days
                  </Form.Text>
                </Form.Group>
              </Col>

              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label><strong>Time Slot</strong> (optional)</Form.Label>
                  <Form.Select
                    value={newLock.timeSlotId}
                    onChange={(e) => setNewLock({ ...newLock, timeSlotId: e.target.value })}
                  >
                    <option value="">All Slots</option>
                    {timeSlots.map(slot => (
                      <option key={slot.id} value={slot.id}>
                        {slot.slot_name} (Cutoff: {slot.cutoff_time})
                      </option>
                    ))}
                  </Form.Select>
                  <Form.Text className="text-muted">
                    Leave blank to lock for all time slots
                  </Form.Text>
                </Form.Group>
              </Col>
            </Row>

            <Alert variant="secondary">
              <strong>Preview:</strong> {newLock.menuItemId ? (
                <>
                  {Object.values(menuItems).flat().find(i => i.id === parseInt(newLock.menuItemId))?.item_name || 'Select item'} will be locked for:{' '}
                  <strong>
                    {newLock.dayOfWeekId 
                      ? daysOfWeek.find(d => d.id === parseInt(newLock.dayOfWeekId))?.day_name
                      : 'All Days'
                    }
                    {' - '}
                    {newLock.timeSlotId
                      ? timeSlots.find(s => s.id === parseInt(newLock.timeSlotId))?.slot_name
                      : 'All Slots'
                    }
                  </strong>
                </>
              ) : (
                'Select a menu item to preview'
              )}
            </Alert>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowAddModal(false)}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleAddLock}>
            Add Lock
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
}

export default ItemLocksTab;
